<template>
    <div>
        <b-modal
            :visible="visible"
            id="upload-pdf-modal"
            :title="`${slideId ? 'Replace' : 'Add'} PDF Slide`"
            size="xl"
            @hide="hidden"
        >
            <div class="row align-items-center">
                <div class="col-lg-12 order-lg-1 order-2">
                    <b-alert v-if="existingUrl" show variant="warning">
                        Updates take time to reach the CDN. Check changes to files here:
                        <a :href="s3FileUrl" target="_blank">
                            AWS Link
                            <font-awesome-icon
                                :icon="['fad', 'external-link']"
                                class="text-warning"
                                fixed-width
                            />
                        </a>
                    </b-alert>
                    <b-alert v-if="errorMessage && !submitting" show variant="danger">
                        There was a problem uploading these slides.
                    </b-alert>
                    <div class="mb-3">
                        PDF File
                        <b-form-file
                            v-model="file"
                            :state="Boolean(file)"
                            placeholder="Choose a file or drop it here..."
                            drop-placeholder="Drop file here..."
                            accept="application/pdf"
                            @input="processFile"
                            :disabled="submitting"
                        ></b-form-file>
                        <b-form-invalid-feedback :state="Boolean(file)">
                            PDF File is required
                        </b-form-invalid-feedback>
                    </div>
                    <b-alert v-if="slideId && slides.length > 1" variant="danger" show>
                        <p class="mb-0">
                            The selected PDF has too many pages ({{ slides.length }}) to replace the existing slide.
                            Please choose one that is only a single page long.
                        </p>
                    </b-alert>
                    <b-alert v-else-if="!slideId && slides.length > 0" variant="warning" show>
                        <span v-if="slides.length > 1">
                            The following {{ slides.length }} pages will be added as individual slides.
                        </span>
                        <span v-else>
                            The following page will be added as a slide.
                        </span>
                    </b-alert>

                    <div class="row">
                        <div v-if="existingUrl" :class="Boolean(file) ? 'col-md-6' : 'col-md-12'">
                            <h5>Current File</h5>
                            <hr>
                            <img
                                v-if="existingUrl"
                                class="w-100 pdf-container"
                                :src="existingUrl"
                            />
                        </div>
                        <div v-if="Boolean(file)" :class="slideId ? 'col-md-6' : 'col-md-12'">
                            <h5>Selected File</h5>
                            <hr>
                            <object
                                class="w-100 pdf-container"
                                v-if="filePreview"
                                type="application/pdf"
                                :data="filePreview"
                            />
                        </div>
                    </div>
                </div>
            </div>

            <template #modal-footer>
                <b-button
                    variant="light"
                    @click="$emit('hide')"
                    :disabled="submitting"
                >
                    Cancel
                </b-button>
                <div v-if="!submitting">
                    <b-button
                        variant="primary"
                        @click="addPdfSlide"
                        :disabled="slides.length === 0 || submitting || (slideId && slides.length > 1)"
                        v-if="!slideId"
                    >
                        Add
                    </b-button>
                    <b-button
                        variant="primary"
                        @click="replacePdfSlide"
                        :disabled="slides.length === 0 || submitting || (slideId && slides.length > 1)"
                        v-else
                    >
                        Replace
                    </b-button>
                </div>
                <div v-else>
                    <b-spinner small label="Uploading..." />
                    Uploading Slides ({{ progressFormatted }}%) - This may take a few minutes
                </div>
            </template>
        </b-modal>
    </div>
</template>

<style>
.pdf-container {
    min-height: 50vh;
    object-fit: contain;
}
</style>

<script>
import {PDFDocument} from 'pdf-lib'
import moment from 'moment'
import {convertCdnUrlToAWS} from '@/common/urlHelpers'

export default {
    data: () => ({
        file: null,
        filePreview: null,
        slides: [],
        submitting: false,
        progress: 0,
        errorMessage: null,
    }),
    props: {
        visible: Boolean,
        article: Object,
        publication: Object,
        slideId: Number,
        existingUrl: String,
    },
    computed: {
        s3FileUrl() {
            if (!this.existingUrl) {
                return ''
            }

            return convertCdnUrlToAWS(this.existingUrl)
        },
        progressFormatted() {
            const numberFormatter = new Intl.NumberFormat('en-US', {maximumFractionDigits: 2})
            return numberFormatter.format(this.progress)
        }
    },
    methods: {
        reset() {
            this.file = null
            this.filePreview = null
            this.slides = []
            this.submitting = false
            this.progress = 0
            this.errorMessage = null
        },
        hidden() {
            this.reset()
            this.$emit('hide')
        },
        async processFile(file) {
            if (!file) {
                return
            }

            // Reset the generated slide state in case process has been called multiple times.
            this.slides = []
            if (this.filePreview) {
                URL.revokeObjectURL(this.filePreview)
            }
            this.filePreview = URL.createObjectURL(file)

            // Each slide must be a single page, so split the PDF into 1-page PDFs.
            const buffer = await file.arrayBuffer()
            const pdf = await PDFDocument.load(buffer)
            for (let i = 0; i < pdf.getPageCount(); i++) {
                const slide = await PDFDocument.create()
                const [page] = await slide.copyPages(pdf, [i])
                slide.addPage(page)
                const slideData = await slide.save()
                const slideBlob = new Blob([slideData.buffer])
                this.slides.push(slideBlob)
            }
        },
        async addPdfSlide() {
            try {
                this.submitting = true

                const baseFileName = this.file.name.replace('.pdf', '')
                const walkthroughId = this.article.id
                const chunkSize = 5
                const slideUploads = []

                for (let chunkStart = 0; chunkStart < this.slides.length; chunkStart += chunkSize) {
                    this.progress = (chunkStart / this.slides.length) * 100
                    const chunk = this.slides.slice(chunkStart, chunkStart + chunkSize)
                    const form = new FormData()
                    form.append('publication_id', this.publication.id)
                    chunk.forEach((slide, i) => {
                        const slideFileName = `${baseFileName}${chunkStart + i + 1}.pdf`
                        form.append('slide[]', slide, slideFileName)
                    })
                    const uploadList = await this.axios.post(`/api/walkthrough/${walkthroughId}/pdf`, form)
                    uploadList.data.forEach((upload) => slideUploads.push(upload))
                }

                await this.$store.dispatch('getWalkthroughSlides', this.article.id)
                await this.showRasterProblems(slideUploads)
                this.$emit('hide')
            } catch (e) {
                const message = `Failed to add slide(s): ${e.message}`
                await this.$store.dispatch('pushError', {
                    data: {message},
                    time: moment().toISOString(),
                })
                this.errorMessage = message
                this.submitting = false
            }
        },
        async replacePdfSlide() {
            try {
                this.submitting = true
                const form = new FormData()
                form.append('slide', this.slides[0], 'slide')

                const response = await this.axios.post(`/api/walkthrough/slide/${this.slideId}/asset`, form)
                await this.showRasterProblems([response.data])
                await this.$store.dispatch('getWalkthroughSlides', this.article.id)
                this.$emit('replaced-asset')
            } catch (e) {
                await this.$store.dispatch('pushError', {
                    data: {
                        message: `Failed to update slide: ${e.message}`,
                    },
                    time: moment().toISOString(),
                })
            } finally {
                this.$emit('hide')
            }
        },
        async showRasterProblems(slideUploads) {
            // this works around an issue where the browser holds on to the cached image
            // from the previous loaded version and winds up displaying that instead of the latest version
            const cacheBusterValue = (new Date()).getTime()
            const slideFilter = (slide) => slide.raster_output
                && (slide.raster_output.includes('warning') || slide.raster_output.includes('error'))
            const problemSlides = slideUploads.filter(slideFilter)
            const problemTable = problemSlides.map((slide) => `
                <tr>
                    <td>${slide.order_number}</td>
                    <td>
                        <a href="${convertCdnUrlToAWS(slide.url)}?sbp_cache_buster=${cacheBusterValue}" target="_blank">
                            <img style="width: 100%;" src="${convertCdnUrlToAWS(slide.url)}?sbp_cache_buster=${cacheBusterValue}" />
                        </a>
                    </td>
                    <td>
                        <textarea style="width: 100%; min-height: 100px; font-size:10px;">
                            ${slide.raster_output}
                        </textarea>
                    </td>
                </tr>
            `)
            if (problemSlides.length > 0) {
                const table = `
                        <div class="mb-2">
                            <p>The uploaded PDF contained pages that had issues converting to PNGs.</p>
                            <p>Please verify they look correct.</p>
                        </div>
                        <table>
                            <thead>
                                <tr>
                                    <th style="width: 10%;">Slide #</th>
                                    <th style="width: 45%;">PNG</th>
                                    <th style="width: 45%;">Output</th>
                                </tr>
                            </thead>
                            <tbody style="max-height: 50vh; overflow-y: scroll;">
                                ${problemTable}
                            </tbody>

                        </table>
                `
                await this.$swal({
                    html: table,
                    showCancelButton: false,
                    confirmButtonColor: '#E67E22',
                    allowOutsideClick: false,
                    customClass: 'swal-even-wider',
                });
            }
        },
    },
    mounted() {
        this.$root.$on('bv::modal::show', (bvEvent, modalId) => {
            if (modalId !== 'upload-pdf-modal') {
                return
            }

            this.reset()
        })
    }
}
</script>
